import React from 'react';
import { Field, useField } from 'formik';
import { Box, IconButton, InputLabel, Typography } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import { cx } from '../../helpers/global';
import { isNil } from '../../utils/global/helper';

interface Props {
  name: string;
  label: string;
  placeholder?: string | undefined;
  disabled: boolean;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isError?: boolean;
  isTouched?: boolean;
  min?: number;
  max?: number;
  step?: number;
  endAdornment?: {
    icon: SvgIconComponent;
    onClick: () => void;
  };
  readOnly?: boolean;
}

const END_ADORNMENT_WIDTH = 40;

export default function TextInput({
  name,
  label,
  placeholder,
  disabled,
  type,
  value,
  onChange,
  isError: isErrorProp,
  isTouched,
  min,
  max,
  step,
  endAdornment,
  readOnly,
}: Props) {
  const [, { error, touched }, { setTouched }] = useField(name);
  const isError = (touched || isTouched) && (!!error || isErrorProp);
  const onBlur = React.useCallback(() => {
    setTouched(true);
  }, [setTouched]);

  const valueProps = !isNil(value) || onChange ? { value, onChange } : {};
  const EndAdornmentIcon = endAdornment?.icon;

  return (
    <div className={cx('input', isError && 'error')}>
      <InputLabel htmlFor={name}>
        <Typography variant="h5">{label}</Typography>
      </InputLabel>
      <Box position="relative" flex={1} display="flex" alignItems="center">
        <Field
          id={name}
          type={type || (name === 'email' ? 'email' : 'text')}
          name={name}
          placeholder={placeholder}
          disabled={disabled}
          onBlur={onBlur}
          {...valueProps}
          min={min}
          max={max}
          step={step}
          style={{
            paddingRight: endAdornment ? END_ADORNMENT_WIDTH : undefined,
            flex: 1,
          }}
          readOnly={readOnly}
        />
        {endAdornment && EndAdornmentIcon && (
          <Box
            p={0.5}
            width={END_ADORNMENT_WIDTH}
            zIndex={6}
            boxSizing="border-box"
            position="absolute"
            right={0}
            top={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center">
            <IconButton
              onClick={e => {
                e.stopPropagation();
                endAdornment.onClick();
              }}
              sx={{
                color: theme => theme.palette.darkGrey.main,
                p: 0.5,
              }}>
              <EndAdornmentIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    </div>
  );
}
